import { Injectable } from '@angular/core';

export interface FileUploadData {
  documentos: File[];
  midia: File | null;
}

export interface FileValidationResult {
  isValid: boolean;
  errors: string[];
}

@Injectable({
  providedIn: 'root'
})
export class FileUploadService {
  
  // Tipos de arquivo permitidos para documentos comprobatórios
  private readonly ALLOWED_DOCUMENT_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  // Tipos de arquivo permitidos para mídia (áudio e vídeo)
  private readonly ALLOWED_MEDIA_TYPES = [
    // Áudios
    'audio/ogg',
    'audio/mpeg',
    'audio/mp3',
    'audio/mp4',
    'audio/aac',
    'audio/x-m4a',
    'audio/wav',
    'audio/webm',
    // Vídeos
    'video/mp4',
    'video/avi',
    'video/mov',
    'video/wmv',
    'video/webm'
  ];

  // Tamanho máximo para documentos (5MB)
  private readonly MAX_DOCUMENT_SIZE = 5 * 1024 * 1024;

  // Tamanho máximo para mídia - áudio e vídeo (50MB)
  private readonly MAX_MEDIA_SIZE = 50 * 1024 * 1024;

  constructor() { }

  /**
   * Valida se os arquivos de documento estão no formato correto
   */
  validateDocuments(files: FileList | File[]): FileValidationResult {
    const errors: string[] = [];
    const fileArray = Array.from(files);

    if (fileArray.length === 0) {
      errors.push('É obrigatório enviar pelo menos um documento');
      return { isValid: false, errors };
    }

    if (fileArray.length > 5) {
      errors.push('Máximo de 5 documentos permitidos');
    }

    fileArray.forEach((file, index) => {
      // Validar tipo de arquivo
      if (!this.ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
        errors.push(`Documento ${index + 1}: Formato não permitido. Use PDF, JPG, JPEG, PNG, DOC ou DOCX`);
      }

      // Validar tamanho do arquivo
      if (file.size > this.MAX_DOCUMENT_SIZE) {
        errors.push(`Documento ${index + 1}: Arquivo muito grande. Máximo 5MB`);
      }

      // Validar nome do arquivo
      if (file.name.length > 100) {
        errors.push(`Documento ${index + 1}: Nome do arquivo muito longo`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Valida se o arquivo de mídia (áudio ou vídeo) está no formato correto
   */
  validateMedia(file: File | null, isRequired: boolean = false): FileValidationResult {
    const errors: string[] = [];

    if (!file) {
      if (isRequired) {
        errors.push('É obrigatório enviar um arquivo de áudio ou vídeo');
      }
      return { isValid: !isRequired, errors };
    }

    // Validar tipo de arquivo
    if (!this.ALLOWED_MEDIA_TYPES.includes(file.type)) {
      errors.push('Formato não permitido. Use áudios (OGG, MP3, M4A, AAC, WAV, WEBM) ou vídeos (MP4, AVI, MOV, WMV, WEBM)');
    }

    // Validar tamanho do arquivo
    if (file.size > this.MAX_MEDIA_SIZE) {
      errors.push('Arquivo muito grande. Máximo 50MB');
    }

    // Validar nome do arquivo
    if (file.name.length > 100) {
      errors.push('Nome do arquivo muito longo');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Valida todos os arquivos de uma vez
   */
  validateAllFiles(documentos: FileList | File[], midia: File | null, midiaRequired: boolean = false): FileValidationResult {
    const documentValidation = this.validateDocuments(documentos);
    const midiaValidation = this.validateMedia(midia, midiaRequired);

    const allErrors = [...documentValidation.errors, ...midiaValidation.errors];

    return {
      isValid: documentValidation.isValid && midiaValidation.isValid,
      errors: allErrors
    };
  }

  /**
   * Prepara os arquivos para envio via FormData
   */
  prepareFormData(documentos: File[], midia: File | null): FormData {
    const formData = new FormData();

    // Adicionar documentos
    documentos.forEach((file, index) => {
      formData.append('evidencias', file, `documento_${index + 1}_${file.name}`);
    });

    // Adicionar mídia (áudio ou vídeo) se existir
    if (midia) {
      let prefix = 'midia';
      if (this.isVideoFile(midia)) {
        prefix = 'video';
      } else if (this.isAudioFile(midia)) {
        prefix = 'audio';
      }
      formData.append('evidencias', midia, `${prefix}_${midia.name}`);
    }

    return formData;
  }

  /**
   * Converte bytes para formato legível
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Obtém a extensão do arquivo
   */
  getFileExtension(filename: string): string {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
  }

  /**
   * Verifica se é um arquivo de imagem
   */
  isImageFile(file: File): boolean {
    return file.type.startsWith('image/');
  }

  /**
   * Verifica se é um arquivo de vídeo
   */
  isVideoFile(file: File): boolean {
    return file.type.startsWith('video/');
  }

  /**
   * Verifica se é um arquivo PDF
   */
  isPdfFile(file: File): boolean {
    return file.type === 'application/pdf';
  }

  /**
   * Verifica se é um arquivo de documento (DOC/DOCX)
   */
  isDocumentFile(file: File): boolean {
    return file.type === 'application/msword' || 
           file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }

  /**
   * Verifica se é um arquivo de áudio
   */
  isAudioFile(file: File): boolean {
    return file.type.startsWith('audio/');
  }
}
